import {Component, effect, signal} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {FloatLabel} from 'primeng/floatlabel';
import {InputText} from 'primeng/inputtext';
import {Slider} from 'primeng/slider';
import {Message} from 'primeng/message';
import {Checkbox} from "primeng/checkbox";
import {InputGroupAddon} from "primeng/inputgroupaddon";
import {InputGroup} from "primeng/inputgroup";
import {InputMask} from "primeng/inputmask";
import {NgxCaptchaModule, ReCaptchaV3Service} from "ngx-captcha";
import {environment} from "../../../environments/environment";
import {AuctionsFormRequest} from "../../../shared/model/auctions-form-request.model";
import {LeasingBudget} from "../../../shared/model/leasing-budget.model";
import {AuctionsFormService} from "./auctions-form.service";
import {ToastType} from "../../../shared/toast/toast-type.enum";
import {ToastService} from "../../../shared/toast/toast.service";
import {finalize} from 'rxjs';
import {GoogleAnalyticsService} from '../../../shared/analytics/google/google-analytics.service';
import {BudgetRange} from '../../../shared/type/budget-range.type';
import {Button} from 'primeng/button';
import {NgIcon, provideIcons} from '@ng-icons/core';
import {tablerHeadset} from '@ng-icons/tabler-icons';
import {
  AnalyticsEventCategory,
  AnalyticsEventName,
  FormEventSendResult
} from '../../../shared/enum/gtm-cookie-consent.enum';
import {PixelService} from '../../../shared/analytics/meta/pixel.service';
import {FormSubmitStatus, PixelEventName} from '../../../shared/analytics/meta/types/pixel.types';

type LeasingFormControls = {
  name: FormControl<string>;
  company: FormControl<string>;
  budgetRange: FormControl<BudgetRange>;
  email: FormControl<string>;
  phone: FormControl<string>;
  consentDataProcessing: FormControl<boolean>;
  consentPrivacyPolicy: FormControl<boolean>;
};

@Component({
  selector: 'app-auctions-form',
  templateUrl: './auctions-form.component.html',
  imports: [
    FormsModule,
    FloatLabel,
    InputText,
    Slider,
    ReactiveFormsModule,
    Message,
    Checkbox,
    InputGroupAddon,
    InputGroup,
    InputMask,
    NgxCaptchaModule,
    Button,
    NgIcon
  ],
  styleUrl: './auctions-form.component.scss',
  providers: [provideIcons({
    tablerHeadset
  })]
})

export class AuctionsFormComponent {

  protected readonly minBudget = 300;
  protected readonly maxBudget = 15000;
  protected readonly minBudgetDefault = 500;
  protected readonly maxBudgetDefault = 2500;
  protected readonly step = 10;
  protected readonly phoneRegex: string | RegExp = '^\\d{3}[\\s-]?\\d{3}[\\s-]?\\d{3}$';

  protected readonly recaptchaSiteKey = environment.recaptcha.siteKey;
  protected readonly privacyPolicyUrl = environment.privacyPolicyUrl;
  private readonly contactFormActionSuccess = "auctions_contact_form_send_success";
  private readonly contactFormActionError = "auctions_contact_form_send_error";
  private readonly CAPTCHA_ID: string = "auctions_contact_form";

  protected isProcessing = signal(false);

  constructor(
    private readonly analyticsService: GoogleAnalyticsService,
    private readonly pixelService: PixelService,
    private readonly toastService: ToastService,
    private readonly auctionsFormService: AuctionsFormService,
    private readonly recaptchaService: ReCaptchaV3Service
  ) {
    effect(() => {
      if (this.isProcessing()) {
        this.contactForm.disable({emitEvent: false});
      } else {
        this.contactForm.enable({emitEvent: false});
      }
    });
  }

  protected readonly contactForm = new FormGroup<LeasingFormControls>(<LeasingFormControls>{
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(50)],
    }),
    company: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(80)],
    }),
    budgetRange: new FormControl<BudgetRange>([this.minBudgetDefault, this.maxBudgetDefault], {
      nonNullable: true,
      validators: Validators.required,
    }),
    email: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email, Validators.maxLength(50)],
    }),
    phone: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.pattern(this.phoneRegex), Validators.maxLength(50)],
    }),
    consentDataProcessing: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: Validators.requiredTrue
    }),
    consentPrivacyPolicy: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: Validators.requiredTrue
    })
  });

  protected formValueAccessor(name: keyof LeasingFormControls) {
    return this.contactForm.controls[name];
  }

  protected get budget(): LeasingBudget {
    const [min, max] = this.contactForm.controls.budgetRange.value;

    return {min, max};
  }

  protected isInvalid(name: keyof LeasingFormControls) {
    const ctrl = this.formValueAccessor(name);
    return (ctrl.touched || this.isProcessing()) && ctrl.invalid;
  }

  protected isError(name: keyof LeasingFormControls): string | null {
    const form = this.formValueAccessor(name);
    if (!(form.touched || this.isProcessing()) || form.valid) return null;
    if (form.hasError('required')) return 'Pole jest wymagane';
    if (form.hasError('minlength')) return 'Za krótka wartość';
    if (form.hasError('maxlength')) return 'Za długa wartość';
    if (form.hasError('rangeOutOfBounds')) return `Zakres poza ${this.minBudget}–${this.maxBudget} zł`;

    return 'Nieprawidłowy format';
  }

  protected onSubmit() {
    this.isProcessing.set(true);

    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    if (this.contactForm.invalid) return;

    this.toastService.fireToast(ToastType.INFO, "Zapytanie", "Wysyłanie zapytania");

    this.recaptchaService.execute(this.recaptchaSiteKey, this.CAPTCHA_ID, (token) => {

      const source = this.contactForm;

      const payload: AuctionsFormRequest = {
        fullName: source.controls.name.getRawValue(),
        company: source.controls.company.getRawValue(),
        budgetRange: {
          min: this.budget.min,
          max: this.budget.max
        },
        email: source.controls.email.getRawValue(),
        phone: source.controls.phone.getRawValue(),
        consentDataProcessing: source.controls.consentDataProcessing.getRawValue(),
        consentPrivacyPolicy: source.controls.consentPrivacyPolicy.getRawValue(),
        recaptchaToken: token
      };

      this.auctionsFormService.sendContactMessage(payload).pipe(
        finalize(() => this.isProcessing.set(false))
      ).subscribe({
        next: (response) => {
          if (response.ok) {
            this.toastService.fireToast(ToastType.SUCCESS, "Zapytanie", "Zapytanie wysłane");
            this.analyticsService.trackEvent({
              event: AnalyticsEventName.AuctionsContactFormSubmit,
              eventCategory: AnalyticsEventCategory.ContactForm,
              eventPayload: FormEventSendResult.Success
            });

            this.pixelService.trackEvent(
              PixelEventName.AuctionsContactFormSubmit, {
                status: FormSubmitStatus.SUCCESS
              });
          }
          this.contactForm.reset();
        },
        error: () => {
          this.toastService.fireToast(ToastType.ERROR, "Zapytanie", "Błąd w wysyłaniu zapytania");
          this.analyticsService.trackEvent({
            event: AnalyticsEventName.AuctionsContactFormSubmit,
            eventCategory: AnalyticsEventCategory.ContactForm,
            eventPayload: FormEventSendResult.Error
          });

          this.pixelService.trackEvent(
            PixelEventName.AuctionsContactFormSubmit, {
              status: FormSubmitStatus.ERROR
            });
        }
      });
    });
  }
}
